import React, { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from 'react'
import { CaseStatusType, CaseType } from '../../types'
import { useAxiosInstance } from '../../api/AxiosInstanceContext';
import { FiltersContext } from '../filtersContext';
import { stored } from '../../constants';
import { SearchContext } from '../searchContext';
import { SortsContext } from '../sortsContext';
import { getApiErrorMessage } from '../../utils/apiError';
import axios from 'axios';

export type ReviewStatusRequest = { state: string; notes?: string; takeover?: boolean; override?: boolean; expectedStatusId?: number };
export type ReviewStatusResult =
    | { ok: true; status?: CaseStatusType }
    | { ok: false; error: string; uncertain?: boolean; conflict?: boolean };

const isCaseStatus = (value: unknown): value is CaseStatusType =>
    !!value && typeof value === 'object' && typeof (value as CaseStatusType).id === 'number'
    && typeof (value as CaseStatusType).state === 'string';

interface ReviewCasesContext {
    reviewCases: CaseType[];
    loadingReviewCases: boolean;
    setLoadingReviewCases: Dispatch<SetStateAction<boolean>>;
    reviewCasesError: string | null;
    refreshReviewCases: () => void;
    recordReviewStatus: (caseId: number, request: ReviewStatusRequest) => Promise<ReviewStatusResult>;
    filteredCases: CaseType[] | undefined;
    setFilteredCases: Dispatch<SetStateAction<CaseType[] | undefined>>;
}

export const ReviewCasesContext = createContext<ReviewCasesContext>(undefined as any)

interface Props {
    children: React.ReactNode
}

export const ReviewCasesProvider: React.FC<Props> = ({ children }) => {
    const axiosInstance = useAxiosInstance();
    const [reviewCases, setReviewCases] = useState([] as CaseType[]);
    const [loadingReviewCases, setLoadingReviewCases] = useState(false);
    const [reviewCasesError, setReviewCasesError] = useState<string | null>(null);
    const [filteredCases, setFilteredCases] = useState<CaseType[] | undefined>();
    const { casesFilters } = useContext(FiltersContext);
    const { casesSorts } = useContext(SortsContext);
    const { caseInfoSubstring } = React.useContext(SearchContext);

    const getReviewCases = () => {
        const reviewCasesFilters = localStorage.getItem(stored.reviewCasesFilters);
        const reviewCasesSorts = localStorage.getItem(stored.reviewCasesSorts);
        const filters = JSON.parse(reviewCasesFilters || '{}');
        const sorts = JSON.parse(reviewCasesSorts || '{}');
        const searchObject = {...filters, ...sorts};
        let updatedJsonString;
        if (caseInfoSubstring) {
            searchObject.search_field = caseInfoSubstring;
            updatedJsonString = searchObject ? JSON.stringify(searchObject) : reviewCasesFilters;
        }
        setLoadingReviewCases(true);
        axiosInstance.post('/review_cases/', updatedJsonString || JSON.stringify(searchObject))
            .then(({ data }) => {
                setReviewCases(data);
                setReviewCasesError(null);
            })
            .catch((e) => {
                console.error('get review cases error:', e);
                setReviewCasesError(getApiErrorMessage(e, 'Cases could not be refreshed. If you just submitted a review, do not submit it again.'));
            }).finally(() => setLoadingReviewCases(false));
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            getReviewCases();
        }, 1500);
        return () => clearTimeout(handler);
    }, [casesFilters, casesSorts, caseInfoSubstring]);


    const refreshReviewCases = () => {
        getReviewCases();
    };

    // Older servers answer with an empty body, so the list refresh stays authoritative.
    const recordReviewStatus = async (
        caseId: number,
        { state, notes = '', takeover, override, expectedStatusId }: ReviewStatusRequest,
    ): Promise<ReviewStatusResult> => {
        let status: CaseStatusType | undefined;
        try {
            const { data } = await axiosInstance.post(
                '/review_add_case_status/',
                JSON.stringify({
                    case_id: caseId, state, notes,
                    ...(takeover ? { takeover: true } : {}),
                    ...(override ? { override: true } : {}),
                    ...(expectedStatusId !== undefined ? { expected_status_id: expectedStatusId } : {}),
                }),
            );
            status = isCaseStatus(data) ? data : undefined;
        } catch (e) {
            const uncertain = !axios.isAxiosError(e) || !e.response || e.response.status >= 500;
            const conflict = axios.isAxiosError(e) && e.response?.status === 409;
            if (conflict) getReviewCases();
            return { ok: false, uncertain, conflict, error: uncertain
                ? 'The change could not be confirmed. Refresh and check the case history before trying again.'
                : getApiErrorMessage(e, 'The change was rejected. Your draft has been kept.') };
        }
        if (status) {
            const saved = status;
            setReviewCases(cases => cases.map(item => item.id === caseId && !item.statuses?.some(existing => existing.id === saved.id)
                ? { ...item, statuses: [...(item.statuses ?? []), saved] }
                : item));
        }
        getReviewCases();
        return { ok: true, status };
    };

    const values = {
        reviewCases: reviewCases ? reviewCases : [],
        loadingReviewCases,
        setLoadingReviewCases,
        reviewCasesError,
        refreshReviewCases,
        recordReviewStatus,
        filteredCases,
        setFilteredCases
    };

    return <ReviewCasesContext.Provider value={values}>{children}</ReviewCasesContext.Provider>;
}
